/** Comments on a clause list and its contradictions (docs/Y2026-Q4/design-clause-comments.md):
 * a comment is kept with the file or folder and applied to the list at once, in light of
 * every earlier comment; the Comments tab is the list's audit trail. */
import { useState } from "react";
import { Link } from "react-router-dom";

import { useAddClauseComment, useRetryClauseComments } from "../api";
import type { ClauseComment, ClauseOutcome, DocumentRegister, RegisterLine } from "../api";
import { formatWhen } from "../utils/formatWhen";
import styles from "./Clauses.module.css";

/** A comment box that opens on a click and says where its comment went. */
export const CommentBox = ({
  documentId,
  registerId,
  kind,
  lineIds,
  label = "Comment",
  placeholder,
}: {
  documentId: string;
  /** The version the comment is made on. */
  registerId: string;
  kind: ClauseComment["kind"];
  lineIds: string[];
  label?: string;
  placeholder: string;
}) => {
  const [open, setOpen] = useState(false);
  const [body, setBody] = useState("");
  const add = useAddClauseComment(documentId);
  if (add.isSuccess && !open) {
    return add.data.not_started ? (
      <p className={styles.note}>
        Comment saved, but the list could not be updated. Try again from the Comments tab.
      </p>
    ) : (
      <p className={styles.note}>
        Comment sent. The list is being updated; follow it on the Comments tab.
      </p>
    );
  }
  if (!open) {
    return (
      <button type="button" className={styles.toggle} onClick={() => setOpen(true)}>
        {label}
      </button>
    );
  }
  return (
    <div className={styles.commentBox}>
      <textarea
        aria-label={label}
        value={body}
        placeholder={placeholder}
        onChange={(e) => setBody(e.target.value)}
      />
      <div className={styles.commentActions}>
        <button
          type="button"
          disabled={!body.trim() || add.isLoading}
          onClick={() =>
            add.mutate(
              { kind, line_ids: lineIds, body, register_id: registerId },
              {
                onSuccess: () => {
                  setBody("");
                  setOpen(false);
                },
              },
            )
          }
        >
          {add.isLoading ? "Sending…" : "Send"}
        </button>
        <button type="button" className={styles.toggle} onClick={() => setOpen(false)}>
          Cancel
        </button>
        {add.isError && <span className={styles.muted}>Not sent: {add.error.message}</span>}
      </div>
    </div>
  );
};

/** Where a clause sits, as a person finds it: its file and page, and its section. */
const whereIs = (line: RegisterLine): string => {
  const at = line.file ? `${line.file.name}, page ${line.file.page}` : `page ${line.page}`;
  const section = line.section_path.length ? ` · ${line.section_path.join(" › ")}` : "";
  return `${at}${section}`;
};

const Side = ({ line, label }: { line: RegisterLine; label: string }) => (
  <div className={styles.contradictionSide}>
    <p className={styles.contradictionWhere}>
      {label} · <Link to={{ search: "?view=document", hash: line.line_id }}>{line.reference}</Link> · {whereIs(line)}
    </p>
    <p className={styles.text}>{line.text}</p>
  </div>
);

/** Why there are none: none found, or the comparison not run on this version. */
const noneFound = (counts: DocumentRegister["counts"]): string => {
  if (counts.pairs_asked === undefined) {
    return "Not checked yet for this version: the next rebuild checks it.";
  }
  return counts.pairs_error
    ? "None found, though some clauses could not be compared."
    : "None found.";
};

/** Every pair of clauses the document says that cannot both be met, each once, with both
 * clauses in the document's own words and where each sits. Always shown. */
export const Contradictions = ({
  documentId,
  register,
  lines,
}: {
  documentId: string;
  register: DocumentRegister;
  lines: RegisterLine[];
}) => {
  const byId = new Map(lines.map((l) => [l.line_id, l]));
  const seen = new Set<string>();
  const pairs = lines.flatMap((line) =>
    line.conflicts.flatMap((ref) => {
      const other = byId.get(ref.line_id);
      const key = [line.line_id, ref.line_id].sort().join("|");
      if (!other || seen.has(key)) return [];
      seen.add(key);
      const [a, b] = line.position < other.position ? [line, other] : [other, line];
      return [{ key, a, b, note: ref.note }];
    }),
  );
  return (
    <section
      className={`${styles.contradictions} ${pairs.length === 0 ? styles.contradictionsNone : ""}`}
      aria-labelledby="contradictions-heading"
    >
      <h3 id="contradictions-heading" className={styles.contradictionsHead}>
        Contradictions to resolve with the client ({pairs.length})
      </h3>
      <p className={styles.note}>
        Clauses that cannot both be met. If one is not a contradiction, comment on it.
      </p>
      {pairs.length === 0 ? (
        <p className={styles.note}>{noneFound(register.counts)}</p>
      ) : (
        pairs.map(({ key, a, b, note }) => (
          <article key={key} className={styles.contradiction}>
            {note && <p className={styles.contradictionWhy}>{note}</p>}
            <div className={styles.contradictionSides}>
              <Side line={a} label="A" />
              <Side line={b} label="B" />
            </div>
            <CommentBox
              documentId={documentId}
              registerId={register.register_id}
              kind="pair"
              lineIds={[a.line_id, b.line_id]}
              label="Comment on this contradiction"
              placeholder="For example: these are different tests, so both can be met; or the 6-day period governs."
            />
          </article>
        ))
      )}
    </section>
  );
};

const STATUS: Record<ClauseComment["status"], [string, string]> = {
  queued: ["Queued", styles.chipWarn],
  applying: ["Updating the list", styles.chipWarn],
  applied: ["Applied", styles.chipGood],
  failed: ["Failed", styles.chipBad],
};

const clauseText = (o: ClauseOutcome | string | null | undefined): string => {
  if (!o) return "not found";
  if (typeof o === "string") return o;
  return `${o.reference}: ${o.statement || o.title || o.classification}`;
};

const Change = ({ comment }: { comment: ClauseComment }) => {
  const outcome = comment.outcome;
  if (comment.status !== "applied") return null;
  if (!outcome || !("before" in outcome)) {
    return (
      <p className={styles.commentChange}>
        Applied in version {comment.applied_version}.
      </p>
    );
  }
  const before = clauseText(outcome.before);
  const after = clauseText(outcome.after);
  return (
    <p className={styles.commentChange}>
      Version {comment.applied_version}:{" "}
      {before === after ? `unchanged (${after})` : `${before} → ${after}`}
    </p>
  );
};

const RetryFailed = ({ documentId }: { documentId: string }) => {
  const retry = useRetryClauseComments(documentId);
  return (
    <p className={styles.note}>
    <button
      type="button"
      className={styles.toggle}
      disabled={retry.isLoading}
      onClick={() => retry.mutate()}
    >
      {retry.isLoading ? "Retrying…" : "Try the waiting and failed comments again"}
    </button>
    {retry.isError && <span className={styles.muted}> Not retried: {retry.error.message}</span>}
    </p>
  );
};

/** Every comment on the list, newest first, and a box for a comment on the whole list. */
export const CommentsPanel = ({
  documentId,
  registerId,
  comments,
}: {
  documentId: string;
  /** The version open on the page, which a comment on the whole list is made on. */
  registerId: string;
  comments: ClauseComment[] | undefined;
}) => (
  <>
    {comments?.some((c) => c.status === "failed" || c.status === "queued") && (
      <RetryFailed documentId={documentId} />
    )}
    <p className={styles.note}>
      Every comment on this list, newest first.
    </p>
    <CommentBox
      documentId={documentId}
      registerId={registerId}
      kind="list"
      lineIds={[]}
      label="Comment on the whole list"
      placeholder="For example: name each milestone as the document does; or a whole exhibit is informative."
    />
    {!comments ? (
      <p className={styles.note}>Loading comments…</p>
    ) : comments.length === 0 ? (
      <p className={styles.note}>No comments yet.</p>
    ) : (
      <ul className={styles.list} aria-label="Comments on this list">
        {comments.map((c) => {
          const [label, tone] = STATUS[c.status];
          return (
            <li key={c.comment_id} className={styles.commentItem}>
              <span className={`${styles.chip} ${tone}`}>{label}</span>{" "}
              <span className={styles.muted}>
                {c.kind === "list" ? "The whole list" : c.kind === "pair" ? "Contradiction " : "Clause "}
                {c.clauses.map((cl, i) => (
                  <span key={cl.line_id}>
                    {i > 0 && " and "}
                    <Link to={{ search: "?view=document", hash: cl.line_id }}>{cl.reference}</Link>
                  </span>
                ))}{" "}
                · {c.author_email} · {c.created_at ? formatWhen(c.created_at) : ""}
                {c.made_on_version !== null ? ` · on version ${c.made_on_version}` : ""}
              </span>
              <p className={styles.commentBody}>{c.body}</p>
              <Change comment={c} />
              {c.error && <p className={styles.commentChange}>Not applied: {c.error}</p>}
            </li>
          );
        })}
      </ul>
    )}
  </>
);
